import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../constants";
import { ItemPath } from "./get-report";

/**
 * WasteSense-specific: only WasteSense payloads embed snapshot images, so this
 * is the sole remaining consumer of the legacy `periodStart` {@link ItemPath}
 * key. A future image-bearing type should get a `reportId`-keyed variant rather
 * than extend this.
 *
 * Returns a short-lived presigned S3 URL for one snapshot derivative. Two
 * guards apply (design §2.3): router middleware confirms the caller is a tenant
 * admin, then the handler 403s (never 404s) unless `key` is referenced by the
 * report's payload or is the `full` sibling of a referenced thumb — so the
 * response never leaks whether arbitrary bucket keys exist.
 */

// `key` stays a plain string here because the structural shape is
// validated by the `SnapshotDerivativeKey` codec in the handler — a Zod
// refinement would only re-run the same parser.
export const SnapshotUrlQuery = z.object({
  key: z.string().min(1),
  size: z.enum(["thumb", "full"]),
});
export type SnapshotUrlQuery = z.output<typeof SnapshotUrlQuery>;

// `expiresInSeconds` is named in full so the unit is unambiguous to
// consumers — it's how long the frontend has to refetch before the URL
// dies.
export const SnapshotUrlResponse = z.object({
  data: z.object({
    url: z.string(),
    expiresInSeconds: z.int().positive(),
  }),
});
export type SnapshotUrlResponse = z.output<typeof SnapshotUrlResponse>;

export const reportsSnapshotUrlResource = defineResource({
  feature: FEATURE_NAME,
  resource: "{type}/items/{periodStart}/snapshot-url",
  resourcePathSchema: ItemPath,
  scope: "tenant",
});

export const getReportSnapshotUrlEndpoint = defineEndpoint(reportsSnapshotUrlResource, {
  responseSchema: SnapshotUrlResponse,
  querySchema: SnapshotUrlQuery,
});
